import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { Magic } from 'magic-sdk';
import { toast } from 'react-toastify';
import { api } from '../utils/api';

const AuthContext = createContext();

const magic = new Magic(process.env.REACT_APP_API_KEY, {
  network: {
    rpcUrl: process.env.REACT_APP_RPC_URL,
  },
});

export const AuthProvider = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [email, setEmail] = useState(localStorage.getItem('email'));
  const [role, setRole] = useState(localStorage.getItem('role'));
  const [isLoading, setIsLoading] = useState(true);

  const refreshSession = useCallback(async () => {
    try {
      // Probe the backend with the httpOnly auth cookie. If we get a valid
      // /me response, we are logged in — even if Magic state was lost.
      const { data } = await api.get('me');
      if (data?.email) {
        setEmail(data.email);
        setRole(data.role);
        localStorage.setItem('email', data.email);
        localStorage.setItem('role', data.role);
        localStorage.setItem('isLoggedIn', 'true');
        setIsLoggedIn(true);

        try {
          const info = await magic.user.getInfo();
          setUserInfo(info);
        } catch (_) { /* Magic info is optional */ }
        return true;
      }
    } catch (_) {
      // Falls through to logged-out state
    }
    setIsLoggedIn(false);
    setUserInfo(null);
    return false;
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      await refreshSession();
      if (mounted) setIsLoading(false);
    })();
    return () => { mounted = false; };
  }, [refreshSession]);

  const login = async (emailAddress, setOtpLogin, setShowOTPModal) => {
    try {
      const otpLogin = magic.auth.loginWithEmailOTP({
        email: emailAddress,
        showUI: false,
        deviceCheckUI: false,
      });

      if (setOtpLogin) setOtpLogin(otpLogin);

      otpLogin
        .on('device-needs-approval', () => console.log('device-needs-approval'))
        .on('device-approved',       () => console.log('device-approved'))
        .on('email-otp-sent',        () => { if (setShowOTPModal) setShowOTPModal(true); })
        .on('done', async (did) => {
          try {
            // Backend sets the httpOnly auth cookie on this call.
            const { data } = await api.post('login', {}, {
              headers: { 'Content-Type': 'application/json', Authorization: did },
            });
            const userRole = data?.role || 'user';

            localStorage.setItem('role', userRole);
            localStorage.setItem('isLoggedIn', 'true');
            localStorage.setItem('email', emailAddress);

            try {
              const info = await magic.user.getInfo();
              setUserInfo(info);
            } catch (_) {}

            setIsLoggedIn(true);
            setRole(userRole);
            setEmail(emailAddress);

            toast.success(`Logged in as ${userRole}`);
          } catch (error) {
            if (error?.response?.status === 400) {
              toast.error('Access denied — your email is not invited.');
            } else {
              console.error('Login failed:', error);
              toast.error('Login failed');
            }
            await magic.user.logout().catch(() => {});
          } finally {
            if (setShowOTPModal) setShowOTPModal(false);
            if (setOtpLogin) setOtpLogin(null);
          }
        })
        .on('settled', () => {
          if (setShowOTPModal) setShowOTPModal(false);
          if (setOtpLogin) setOtpLogin(null);
        })
        .catch((err) => {
          console.error(err);
          if (setShowOTPModal) setShowOTPModal(false);
          if (setOtpLogin) setOtpLogin(null);
        });

      return otpLogin;
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Failed to initiate login');
    }
  };

  const logout = async () => {
    try {
      await api.post('logout').catch(() => {});
      await magic.user.logout().catch(() => {});
    } finally {
      setIsLoggedIn(false);
      setUserInfo(null);
      setEmail(null);
      setRole(null);
      ['isLoggedIn', 'role', 'email', 'activePage', 'notes', 'dealData', 'token']
        .forEach((k) => localStorage.removeItem(k));
      toast.success('Signed out');
    }
  };

  return (
    <AuthContext.Provider value={{ isLoggedIn, userInfo, email, role, login, logout, magic, isLoading, refreshSession }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
